import type { ApprovalStatus, UserRole } from "@/hooks/useUserRole";
import {
  getActiveWorkspace,
  type ActiveWorkspace,
} from "@/lib/workspacePreference";

export type UserRoleRow = {
  role: UserRole;
  approval_status: ApprovalStatus;
  is_banned: boolean;
  ban_reason: string | null;
};

export type PartnerAccessOptions = {
  isPartner?: boolean;
};

export function mapSingleRoleRowToRows(roleData: {
  role: string | null;
  approval_status: string | null;
}): UserRoleRow[] {
  if (!roleData.role) return [];
  return [
    {
      role: roleData.role as UserRoleRow["role"],
      approval_status: (roleData.approval_status ?? null) as ApprovalStatus,
      is_banned: false,
      ban_reason: null,
    },
  ];
}

export function isRoleApproved(row: UserRoleRow | null | undefined): boolean {
  return row?.approval_status === "approved" && !row?.is_banned;
}

export function findRoleRow(
  rows: UserRoleRow[],
  role: UserRole,
): UserRoleRow | null {
  return rows.find((row) => row.role === role) ?? null;
}

export function hasApprovedRole(rows: UserRoleRow[], role: UserRole): boolean {
  return isRoleApproved(findRoleRow(rows, role));
}

export function hasApprovedPfaWorkspace(rows: UserRoleRow[]): boolean {
  return hasApprovedRole(rows, "pfa");
}

/** CFA role exists and is not rejected (pending or approved). */
export function hasPfaWorkspace(rows: UserRoleRow[]): boolean {
  const pfa = findRoleRow(rows, "pfa");
  if (!pfa || pfa.is_banned) return false;
  return pfa.approval_status === "pending" || pfa.approval_status === "approved";
}

export function hasApprovedPartnerWorkspace(
  rows: UserRoleRow[],
  { isPartner = false }: PartnerAccessOptions = {},
): boolean {
  return isPartner && hasApprovedRole(rows, "pfa");
}

/** Approved CFA + partner flag — full workspace switch. */
export function canSwitchWorkspace(
  rows: UserRoleRow[],
  { isPartner = false }: PartnerAccessOptions = {},
): boolean {
  return hasApprovedRole(rows, "pfa") && isPartner;
}

export function pickPrimaryRoleRow(
  rows: UserRoleRow[],
  { isPartner = false }: PartnerAccessOptions = {},
): UserRoleRow | null {
  if (!rows.length) return null;

  const admin = findRoleRow(rows, "admin");
  if (admin) return admin;

  const tester = findRoleRow(rows, "tester");
  if (isRoleApproved(tester)) return tester;

  const approvedPfa = findRoleRow(rows, "pfa");
  if (isPartner && isRoleApproved(approvedPfa) && canSwitchWorkspace(rows, { isPartner })) {
    const workspace = getActiveWorkspace();
    if (workspace === "partner" || workspace === "pfa") {
      return approvedPfa;
    }
  }

  const approved = rows.find((row) => isRoleApproved(row));
  if (approved) return approved;

  return rows[0] ?? null;
}

export function canAccessProtectedRole(
  rows: UserRoleRow[],
  allowedRole: UserRole,
  { isPartner = false }: PartnerAccessOptions = {},
): { allowed: boolean; approvalStatus: ApprovalStatus } {
  if (allowedRole === "admin") {
    const admin = findRoleRow(rows, "admin");
    return {
      allowed: admin?.role === "admin",
      approvalStatus: admin?.approval_status ?? null,
    };
  }

  if (allowedRole === "pfa") {
    const pfa = findRoleRow(rows, "pfa");
    return {
      allowed: !!pfa,
      approvalStatus: pfa?.approval_status ?? null,
    };
  }

  if (allowedRole === "referrals") {
    const pfa = findRoleRow(rows, "pfa");
    return {
      allowed: isPartner && !!pfa,
      approvalStatus: pfa?.approval_status ?? null,
    };
  }

  if (allowedRole === "caterer") {
    const caterer = findRoleRow(rows, "caterer");
    return {
      allowed: !!caterer,
      approvalStatus: caterer?.approval_status ?? null,
    };
  }

  if (allowedRole === "tester") {
    const tester = findRoleRow(rows, "tester");
    return {
      allowed: isRoleApproved(tester),
      approvalStatus: tester?.approval_status ?? null,
    };
  }

  return { allowed: false, approvalStatus: null };
}

export function resolvePostAuthDestination(
  rows: UserRoleRow[],
  isPortalApp: boolean,
  { isPartner = false }: PartnerAccessOptions = {},
): ActiveWorkspace | "admin" | "pending" | "caterer" | "tester" | null {
  const primary = pickPrimaryRoleRow(rows, { isPartner });
  if (!primary) return null;

  if (primary.role === "admin") return "admin";

  if (primary.role === "tester" || hasApprovedRole(rows, "tester")) return "tester";

  if (canSwitchWorkspace(rows, { isPartner })) {
    if (isPortalApp) return "partner";
    return getActiveWorkspace();
  }

  if (isPartner && hasApprovedRole(rows, "pfa")) {
    if (isPortalApp) return "partner";
  }

  if (hasApprovedRole(rows, "pfa")) {
    if (isPortalApp && isPartner) return "partner";
    return "pfa";
  }

  if (hasApprovedRole(rows, "caterer")) return "caterer";

  const pfaRow = findRoleRow(rows, "pfa");
  if (pfaRow?.approval_status === "rejected") {
    return "pending";
  }

  if (pfaRow?.approval_status === "pending") {
    if (isPartner && isPortalApp) return "pending";
    return "pfa";
  }

  if (rows.some((row) => row.approval_status === "pending" || row.approval_status === "rejected")) {
    return "pending";
  }

  return null;
}
